import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import {NgClass} from "@angular/common";
import {FormsModule} from "@angular/forms";
import {DateAdapter} from "@angular/material/core";
import {MatCheckbox} from "@angular/material/checkbox";
import {MatFormField, MatHint, MatSuffix} from "@angular/material/form-field";
import {MatInput} from "@angular/material/input";
import {
  MatDatepicker,
  MatDatepickerInput,
  MatDatepickerToggle,
} from "@angular/material/datepicker";
import {Subscription, exhaustMap, firstValueFrom, takeWhile, timer} from "rxjs";
import {TranslateModule} from "@ngx-translate/core";
import {
  clampDateToToday,
  getDateSafely,
  getWorkReleaseDateSafely,
} from "src/app/helpers/date";
import {MyDateAdapter} from "src/app/helpers/MyDateAdapter";
import type {Book} from "src/app/models/Book";
import type {Comic} from "src/app/models/Comic";
import type {Game} from "src/app/models/Game";
import type {Manga} from "src/app/models/Manga";
import type {Series} from "src/app/models/Series";
import type {WorksModel} from "src/app/models/WorksModel";
import type {PaginatedResponse} from "src/app/modules/shared/interfaces/ApiResponse.interface";
import type {User} from "src/app/modules/auth/models/User";
import {StyledRangeDirective} from "src/app/modules/core/directives/styled-range.directive";
import {ModalService} from "src/app/modules/core/services/modal.service";
import {ToastService} from "src/app/modules/core/services/toast.service";
import type {
  WorkCompanion,
  WorkCompanionOptimisticRemoval,
} from "src/app/modules/works-companions/interfaces/work-companion.interface";
import {WorksCompanionService} from "src/app/modules/works-companions/services/works-companion.service";
import {getWorksCompanionTitleKey} from "src/app/modules/works-companions/utils/works-companion-title.util";
import {UserChipComponent} from "../user-chip/user-chip.component";
import {BookService} from "../../services/book.service";
import {ComicService} from "../../services/comic.service";
import {GameService} from "../../services/game.service";
import {MangaService} from "../../services/manga.service";
import {SeriesService} from "../../services/series.service";
import {WorksService} from "../../services/works.service";
import type {
  WorkQuickCompletionOption,
  WorkQuickCompletionStatus,
} from "../../interfaces/work-quick-completion.interface";
import * as quickCompletion from "../../utils/work-quick-completion.util";
import {
  isBook,
  isComic,
  isGame,
  isManga,
} from "src/app/utils/works-type-guards.util";

@Component({
  selector: "app-work-quick-completion",
  templateUrl: "./work-quick-completion.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{provide: DateAdapter, useClass: MyDateAdapter}],
  imports: [
    FormsModule,
    MatCheckbox,
    MatDatepicker,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatFormField,
    MatHint,
    MatInput,
    MatSuffix,
    NgClass,
    TranslateModule,
    StyledRangeDirective,
    UserChipComponent,
  ],
})
export class WorkQuickCompletionComponent
  implements OnChanges, OnDestroy, AfterViewChecked
{
  @ViewChild("scrollContent")
  private scrollContent?: ElementRef<HTMLElement>;
  @Input({required: true}) public user!: User;
  @Input({required: true}) public work!: WorksModel;
  @Input() public language = "en";
  @Output() public workUpdated = new EventEmitter<WorksModel>();
  @Output() public requestRemove = new EventEmitter<void>();

  public status: WorkQuickCompletionStatus = "ongoing";
  public volumeValue = 1;
  public selectedSeason = 1;
  public selectedEpisode = 1;
  public discoveryDate: Date | null = new Date();
  public discoveredOnRelease = false;
  public readonly today = new Date();
  public isSubmitting = false;
  public isMetadataLoading = false;
  public isSaved = false;
  public hasScrollableContent = false;
  public isContentScrolledToBottom = true;
  public companions: WorkCompanion[] = [];
  public readonly options: WorkQuickCompletionOption[] = [
    {status: "ongoing", labelKey: "WORK_QUICK_COMPLETION_ONGOING"},
    {status: "completed", labelKey: "WORK_QUICK_COMPLETION_DONE"},
    {status: "disabled", labelKey: "ABANDONED"},
  ];

  public get completedLabelKey(): string {
    if (quickCompletion.isSeriesWork(this.work))
      return "PROGRESS_ACTION.ALL_SEEN";
    if (this.work.type === "mangas" || this.work.type === "comics")
      return "PROGRESS_ACTION.ALL_READ";
    return "WORK_QUICK_COMPLETION_DONE";
  }

  private readonly subscriptions: Subscription[] = [];
  private readonly removingCompanionIds = new Set<string>();
  private metadataLoadSubscription?: Subscription;
  private controlsTouched = false;
  private manualDiscoveryDate = new Date();

  constructor(
    private readonly bookService: BookService,
    private readonly comicService: ComicService,
    private readonly gameService: GameService,
    private readonly mangaService: MangaService,
    private readonly seriesService: SeriesService,
    private readonly worksService: WorksService,
    private readonly worksCompanionService: WorksCompanionService,
    private readonly modalService: ModalService,
    private readonly toastService: ToastService,
    private readonly cdr: ChangeDetectorRef
  ) {
    const companionModalSubscription = this.modalService.modalOpened.subscribe(
      (event) => {
        if (event?.modalId === this.companionModalId && !event.opened) {
          this.loadCompanions();
        }
      }
    );
    this.subscriptions.push(companionModalSubscription);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["work"] && this.work) {
      this.resetControls();
      this.loadCompanions();
      this.loadFullWork();
    }
  }

  public get companionModalId(): string {
    return `quick_completion_companion_${this.work?._id ?? "default"}`;
  }

  public get title(): string {
    return this.worksService.getItemTitle(this.work);
  }

  public get showSeriesControls(): boolean {
    return (
      this.status === "ongoing" &&
      quickCompletion.isSeriesWork(this.work) &&
      this.totalSeriesSeasons > 0
    );
  }

  public get showVolumeControls(): boolean {
    return (
      this.status === "ongoing" &&
      (isManga(this.work) || isComic(this.work)) &&
      this.totalVolumes > 0
    );
  }

  public get shouldShowCompletionMetadataLoader(): boolean {
    return (
      this.status === "ongoing" &&
      (this.isSeriesMetadataPending || this.isVolumeMetadataPending)
    );
  }

  public get volumeChoices(): number[] {
    return isManga(this.work) || isComic(this.work)
      ? quickCompletion.getReleasedVolumeNumbers(this.work)
      : [];
  }

  public get seasonChoices(): number[] {
    return quickCompletion.isSeriesWork(this.work)
      ? quickCompletion.getSeriesSeasonNumbers(this.work)
      : [];
  }

  public get episodeChoices(): number[] {
    return quickCompletion.isSeriesWork(this.work)
      ? quickCompletion.getSeriesEpisodeNumbers(this.work, this.selectedSeason)
      : [];
  }

  public get totalVolumes(): number {
    return this.volumeChoices.at(-1) ?? 0;
  }

  public get totalSeriesSeasons(): number {
    return this.seasonChoices.at(-1) ?? 0;
  }

  public get totalSeriesEpisodes(): number {
    return this.episodeChoices.at(-1) ?? 0;
  }

  public get workReleaseDate(): Date | null {
    return getWorkReleaseDateSafely(this.work?.releaseDate);
  }

  public get isSeriesMetadataPending(): boolean {
    return (
      this.isMetadataLoading &&
      quickCompletion.isSeriesWork(this.work) &&
      !quickCompletion.hasSeriesMetadata(this.work)
    );
  }

  public get isVolumeMetadataPending(): boolean {
    return (
      this.isMetadataLoading &&
      (isManga(this.work) || isComic(this.work)) &&
      !quickCompletion.hasVolumeMetadata(this.work)
    );
  }

  public get companionTitleKey(): string {
    return getWorksCompanionTitleKey(this.work?.type);
  }

  public get activeCompanions(): WorkCompanion[] {
    return this.companions.filter(
      (companion) =>
        companion.status !== "removed" && companion.status !== "declined"
    );
  }

  public get canSubmit(): boolean {
    return (
      Boolean(this.user?._id) &&
      Boolean(this.work?._id) &&
      quickCompletion.isSupportedWork(this.work) &&
      Boolean(getDateSafely(this.discoveryDate)) &&
      !this.isSubmitting &&
      !this.isMetadataLoading &&
      quickCompletion.hasSelectableProgress(this.work, this.status)
    );
  }

  public setStatus(status: WorkQuickCompletionStatus): void {
    this.status = status;
    this.controlsTouched = true;
    this.isSaved = false;
    if (status === "ongoing") {
      this.selectedEpisode = Math.min(
        this.selectedEpisode,
        this.totalSeriesEpisodes
      );
    }
    this.cdr.detectChanges();
    this.updateScrollState();
  }

  public onVolumeChange(value: string | number): void {
    this.volumeValue = this.volumeChoices[Number(value)] ?? 0;
    this.controlsTouched = true;
    this.isSaved = false;
    this.cdr.markForCheck();
  }

  public onSeasonChange(value: string | number): void {
    this.selectedSeason = this.seasonChoices[Number(value)] ?? 0;
    this.selectedEpisode = this.episodeChoices.includes(this.selectedEpisode)
      ? this.selectedEpisode
      : this.episodeChoices.at(-1) ?? 0;
    this.controlsTouched = true;
    this.isSaved = false;
    this.cdr.markForCheck();
  }

  public onEpisodeChange(value: string | number): void {
    this.selectedEpisode = this.episodeChoices[Number(value)] ?? 0;
    this.controlsTouched = true;
    this.isSaved = false;
    this.cdr.markForCheck();
  }

  public onDiscoveryDateChange(value: Date | null): void {
    this.discoveryDate = value ? clampDateToToday(value) : null;
    if (this.discoveryDate) {
      this.manualDiscoveryDate = this.discoveryDate;
    }
    this.controlsTouched = true;
    this.isSaved = false;
    this.cdr.markForCheck();
  }

  public onDiscoveredOnReleaseChange(checked: boolean): void {
    const releaseDate = this.workReleaseDate;
    if (checked && !releaseDate) {
      return;
    }

    this.discoveredOnRelease = checked;
    if (checked && releaseDate) {
      if (this.discoveryDate) {
        this.manualDiscoveryDate = this.discoveryDate;
      }
      this.discoveryDate = releaseDate;
    } else {
      this.discoveryDate = this.manualDiscoveryDate;
    }

    this.controlsTouched = true;
    this.isSaved = false;
    this.cdr.markForCheck();
  }

  public scrollToCompanions(): void {
    const element = this.scrollContent?.nativeElement;
    element?.scrollTo({top: element.scrollHeight, behavior: "smooth"});
  }

  public saveCompletion(): void {
    if (!this.canSubmit) {
      return;
    }

    this.isSubmitting = true;
    this.cdr.markForCheck();

    this.saveDiscoveryDate();
  }

  private saveProgress(): void {
    if (isBook(this.work)) {
      this.saveBook(this.work);
      return;
    }

    if (isGame(this.work)) {
      this.saveGame(this.work);
      return;
    }

    if (isManga(this.work) || isComic(this.work)) {
      this.saveVolumes(this.work);
      return;
    }

    if (quickCompletion.isSeriesWork(this.work)) {
      this.saveSeries(this.work);
      return;
    }

    this.handleSaveError();
  }

  private saveDiscoveryDate(): void {
    const discoveryDate = quickCompletion.getAdjustedDiscoveryDate(
      this.discoveryDate,
      this.workReleaseDate
    );
    const releaseDate = this.workReleaseDate;
    if (!discoveryDate) {
      this.handleSaveError();
      return;
    }

    const subscription = this.worksService
      .updateWorkDateByType(
        this.user._id,
        this.work._id,
        quickCompletion.getMetadataFilterType(this.work),
        discoveryDate.toISOString(),
        releaseDate ?? undefined,
        this.status === "completed",
        this.discoveredOnRelease
      )
      .subscribe({
        next: () => {
          if (this.status === "completed") {
            this.handleSaveSuccess(
              quickCompletion.completeWorkAtDate(
                this.work,
                discoveryDate,
                this.discoveredOnRelease
              )
            );
            return;
          }
          this.work = {...this.work, discoveryDate};
          this.saveProgress();
        },
        error: () => this.handleSaveError(),
      });
    this.subscriptions.push(subscription);
  }

  public openCompanionFlow(): void {
    if (!this.work?._id) {
      return;
    }

    this.modalService.openItemModal(this.companionModalId, this.work);
  }

  public onCompanionModalClosed(): void {
    this.loadCompanions();
  }

  public getCompanionDisplayName(companion: WorkCompanion): string {
    return companion.target.displayName || companion.target.username || "";
  }

  public trackCompanion(_index: number, companion: WorkCompanion): string {
    return companion._id;
  }

  public async removeCompanion(companion: WorkCompanion): Promise<void> {
    if (!this.canRemoveCompanion(companion)) {
      return;
    }

    const previousCompanions = [...this.companions];
    const optimisticRemoval = this.getOptimisticRemoval(companion);
    const removalRequest = firstValueFrom(
      this.worksCompanionService.removeCompanion(
        this.user._id,
        this.work._id,
        companion._id
      )
    ).then(() => undefined);

    this.removingCompanionIds.add(companion._id);
    this.worksCompanionService.registerOptimisticRemoval({
      ...optimisticRemoval,
      completion: removalRequest,
    });
    this.companions = this.companions.filter(
      (currentCompanion) => currentCompanion._id !== companion._id
    );
    this.cdr.markForCheck();

    try {
      await removalRequest;
      this.worksCompanionService.clearOptimisticRemoval(optimisticRemoval);
      this.worksCompanionService.notifyCompanionsChanged();
      this.loadCompanions();
    } catch {
      this.worksCompanionService.clearOptimisticRemoval(optimisticRemoval);
      this.companions = previousCompanions;
      this.toastService.sendToast({
        text: "ERROR_UPDATE",
        classes: "bg-form-error",
      });
    } finally {
      this.removingCompanionIds.delete(companion._id);
      this.cdr.markForCheck();
    }
  }

  private canRemoveCompanion(companion: WorkCompanion): boolean {
    return (
      Boolean(this.user?._id) &&
      Boolean(this.work?._id) &&
      Boolean(companion?._id) &&
      !this.removingCompanionIds.has(companion._id)
    );
  }

  private getOptimisticRemoval(
    companion: WorkCompanion
  ): WorkCompanionOptimisticRemoval {
    return {
      userId: this.user._id,
      workId: this.work._id,
      companionId: companion._id,
      targetId: companion.target._id,
      targetType: companion.target.type,
    };
  }

  private resetControls(): void {
    const today = new Date();
    this.status = "ongoing";
    this.volumeValue =
      isManga(this.work) || isComic(this.work)
        ? quickCompletion.getInitialVolumeValue(this.work)
        : 1;
    this.selectedSeason = quickCompletion.isSeriesWork(this.work)
      ? quickCompletion.getInitialSeriesSeason(this.work)
      : 1;
    this.selectedEpisode = quickCompletion.isSeriesWork(this.work)
      ? quickCompletion.getInitialSeriesEpisode(this.work, this.selectedSeason)
      : 1;
    this.discoveryDate = today;
    this.manualDiscoveryDate = today;
    this.discoveredOnRelease = false;
    this.isSubmitting = false;
    this.isSaved = false;
    this.controlsTouched = false;
  }

  private saveBook(work: Book): void {
    const progress = quickCompletion.getSimpleProgress(this.status, "read");
    const subscription = this.bookService
      .updateUserBookProgress(work, this.user, progress)
      .subscribe({
        next: (response) => {
          this.handleSaveSuccess({
            ...work,
            timeSummary: response.timeSummary,
            progress: response.progress ?? undefined,
          });
        },
        error: () => this.handleSaveError(),
      });
    this.subscriptions.push(subscription);
  }

  private saveGame(work: Game): void {
    const progress = quickCompletion.getSimpleProgress(
      this.status,
      "mainStory"
    );
    const subscription = this.gameService
      .updateUserGameProgress(work, this.user, progress)
      .subscribe({
        next: (response) => {
          this.handleSaveSuccess({
            ...work,
            timeSummary: response.timeSummary,
            progress: response.progress ?? undefined,
            playtimeMinutes: response.playtimeMinutes,
          });
        },
        error: () => this.handleSaveError(),
      });
    this.subscriptions.push(subscription);
  }

  private saveSeries(work: Series): void {
    const progress = quickCompletion.getSeriesProgress(
      work,
      this.status,
      this.selectedSeason,
      this.selectedEpisode
    );
    const subscription = this.seriesService
      .updateUserSeriesProgress(
        work,
        this.user,
        progress,
        this.discoveredOnRelease
      )
      .subscribe({
        next: (user) => {
          this.handleSaveSuccess({
            ...work,
            progress:
              quickCompletion.findSeriesProgress(user, work._id) ?? progress,
          });
        },
        error: () => this.handleSaveError(),
      });
    this.subscriptions.push(subscription);
  }

  private saveVolumes(work: Manga | Comic): void {
    const volumes = quickCompletion.getSelectedVolumes(
      this.status,
      this.totalVolumes,
      this.volumeValue,
      this.volumeChoices
    );
    const progress = quickCompletion.buildVolumeProgress(
      work.progress,
      volumes,
      this.status
    );
    const service = isManga(work) ? this.mangaService : this.comicService;
    const subscription = service
      .updateUserProgress(
        work,
        this.user,
        volumes,
        "patch",
        this.status,
        this.discoveredOnRelease
      )
      .subscribe({
        next: (user) => {
          const savedProgress = isManga(work)
            ? quickCompletion.findMangaProgress(user, work._id)
            : quickCompletion.findComicProgress(user, work._id);
          this.handleSaveSuccess({
            ...work,
            progress: savedProgress ?? progress,
          });
        },
        error: () => this.handleSaveError(),
      });
    this.subscriptions.push(subscription);
  }

  private handleSaveSuccess(updatedWork: WorksModel): void {
    this.work = updatedWork;
    this.isSubmitting = false;
    this.isSaved = true;
    this.workUpdated.emit(updatedWork);
    this.cdr.markForCheck();
  }

  private handleSaveError(): void {
    this.isSubmitting = false;
    this.toastService.sendToast({
      text: "ERROR_UPDATE",
      classes: "bg-form-error",
    });
    this.cdr.markForCheck();
  }

  private loadFullWork(): void {
    this.metadataLoadSubscription?.unsubscribe();

    if (!this.shouldLoadCompletionMetadata()) {
      this.isMetadataLoading = false;
      return;
    }

    const workId = this.work._id;
    const filters = JSON.stringify({
      workType: [quickCompletion.getMetadataFilterType(this.work)],
    });

    this.isMetadataLoading = true;
    this.cdr.markForCheck();

    this.metadataLoadSubscription = timer(0, 1500)
      .pipe(
        exhaustMap(() =>
          this.worksService.getWorks(this.user._id, {
            workId,
            filters,
          })
        ),
        takeWhile(
          (response) =>
            quickCompletion.shouldContinueMetadataPolling(response, this.work),
          true
        )
      )
      .subscribe({
        next: (response) => this.applyLoadedWork(response),
        error: () => {
          this.isMetadataLoading = false;
          this.cdr.markForCheck();
        },
        complete: () => {
          this.isMetadataLoading = false;
          this.cdr.markForCheck();
        },
      });
  }

  private shouldLoadCompletionMetadata(): boolean {
    return (
      Boolean(this.user?._id) &&
      Boolean(this.work?._id) &&
      quickCompletion.hasCompletionMetadataControls(this.work) &&
      !quickCompletion.hasCompletionMetadata(this.work)
    );
  }

  private applyLoadedWork(response: PaginatedResponse | null): void {
    const loadedWork = quickCompletion.getLoadedWork(
      response,
      this.work?._id?.toString()
    );

    if (!loadedWork) {
      return;
    }

    this.work = loadedWork;

    if (this.controlsTouched) {
      this.clampCurrentControls();
    } else {
      this.resetControls();
    }

    this.cdr.markForCheck();
  }

  private clampCurrentControls(): void {
    if (isManga(this.work) || isComic(this.work)) {
      this.volumeValue = quickCompletion.clampReleasedNumber(
        this.volumeValue,
        this.volumeChoices
      );
    }
    if (quickCompletion.isSeriesWork(this.work)) {
      this.selectedSeason = quickCompletion.clampReleasedNumber(
        this.selectedSeason,
        this.seasonChoices
      );
      this.selectedEpisode = quickCompletion.clampReleasedNumber(
        this.selectedEpisode,
        this.episodeChoices
      );
    }
  }

  private loadCompanions(): void {
    if (!this.user?._id || !this.work?._id) {
      this.companions = [];
      return;
    }

    const subscription = this.worksCompanionService
      .getCompanions(this.user._id, this.work._id)
      .subscribe({
        next: (companions) => {
          this.companions = companions ?? [];
          this.cdr.markForCheck();
        },
        error: () => {
          this.companions = [];
          this.cdr.markForCheck();
        },
      });
    this.subscriptions.push(subscription);
  }

  ngAfterViewChecked(): void {
    this.updateScrollState();
  }

  public updateScrollState(): void {
    const element = this.scrollContent?.nativeElement;
    if (!element) {
      return;
    }

    const hasScrollableContent =
      element.scrollHeight > Math.ceil(element.clientHeight + 4);
    const isContentScrolledToBottom =
      !hasScrollableContent ||
      Math.ceil(element.scrollTop + element.clientHeight) >=
        element.scrollHeight;

    if (
      this.hasScrollableContent === hasScrollableContent &&
      this.isContentScrolledToBottom === isContentScrolledToBottom
    ) {
      return;
    }

    this.hasScrollableContent = hasScrollableContent;
    this.isContentScrolledToBottom = isContentScrolledToBottom;
    this.cdr.markForCheck();
  }

  ngOnDestroy(): void {
    this.metadataLoadSubscription?.unsubscribe();
    for (const subscription of this.subscriptions) {
      subscription.unsubscribe();
    }
  }
}
